/**
 * The list filters as URL state (Story 20, task 88).
 *
 * The Alerts queue and the Detections stream keep their filters in the
 * URL, so the Overview can link into a filtered queue, a filtered view can
 * be shared, and Back from a detail screen returns to it. Parsing is
 * lenient: an unknown value falls back to the default, never to an error
 * page. Defaults are left out of the URL entirely, so `/alerts` is the
 * unfiltered queue and a link carries only what it means.
 *
 * The date range is a preset in the URL and an ISO `since` on the wire.
 * The SPA turns the preset into a timestamp at request time, so the API
 * never sees a preset name and "30d" means the same thing on both sides
 * of midnight (design r5).
 */

import { isSeverityFilter, type SeverityFilterValue } from '../components/SeverityFilter';

export const RANGE_PRESETS = ['7d', '30d', '60d', '90d', 'all'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '60d', label: 'Last 60 days' },
  { value: '90d', label: 'Last 90 days' },
];

const RANGE_DAYS: Record<Exclude<RangePreset, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '60d': 60,
  '90d': 90,
};

/** The preset as an ISO lower bound, computed now; `all` is no bound. */
export function sinceFor(range: RangePreset, now: Date = new Date()): string | undefined {
  if (range === 'all') return undefined;
  return new Date(now.getTime() - RANGE_DAYS[range] * 86_400_000).toISOString();
}

export const ALERT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active', label: 'Active (open, triaged, in progress)' },
  { value: 'open', label: 'Open' },
  { value: 'triaged', label: 'Triaged' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'closed', label: 'Closed (resolved, false positive)' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'false_positive', label: 'False positive' },
  { value: 'all', label: 'All statuses' },
];

export const DISPOSITION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any disposition' },
  { value: 'actionable', label: 'Actionable' },
  { value: 'suspicious', label: 'Suspicious' },
];

export const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All sources' },
  { value: 'defender', label: 'Defender XDR' },
  { value: 'sentinelone', label: 'SentinelOne' },
];

/**
 * Story 45's `service` filter (task 179c): every engine detection's source
 * is `bluesphere`, so only the service tells a DLP rule from another.
 */
export const SERVICE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All services' },
  { value: 'mdr', label: 'MDR' },
  { value: 'latticeai_dlp', label: 'LatticeAI DLP' },
];

export const OUTCOME_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any outcome' },
  { value: 'raised', label: 'Raised an alert' },
  { value: 'resolved', label: 'Resolved by playbook' },
];

export const DEFAULT_ALERT_STATUS = 'active';

export interface AlertsSearch {
  status?: string;
  severity?: Exclude<SeverityFilterValue, 'All'>;
  disposition?: string;
  source?: string;
  service?: string;
  range?: Exclude<RangePreset, 'all'>;
  /**
   * `all` shows every visible Client's queue even while a Client is
   * selected — the beacon's click-through (task 106), which must show
   * everything it counted without touching the selection.
   */
  view?: 'all';
}

export interface DetectionsSearch {
  severity?: Exclude<SeverityFilterValue, 'All'>;
  source?: string;
  service?: string;
  outcome?: string;
  range?: Exclude<RangePreset, 'all'>;
  /** Filter the stream to one provider incident; set by the detail's incident link. */
  incident?: string;
}

/** A value from `options`, or undefined when absent, unknown or the default. */
function pick(raw: unknown, options: Array<{ value: string }>, fallback = ''): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim().toLowerCase();
  if (value === fallback) return undefined;
  return options.some((o) => o.value === value) ? value : undefined;
}

/** `high`, `HIGH` and `High` all mean the High band; `All` and junk mean no filter. */
function pickSeverity(raw: unknown): AlertsSearch['severity'] {
  if (typeof raw !== 'string' || !raw) return undefined;
  const band = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  return isSeverityFilter(band) && band !== 'All' ? band : undefined;
}

function pickRange(raw: unknown): AlertsSearch['range'] {
  const value = pick(raw, RANGE_OPTIONS, 'all');
  return value as AlertsSearch['range'];
}

export function parseAlertsSearch(search: Record<string, unknown>): AlertsSearch {
  return {
    status: pick(search.status, ALERT_STATUS_OPTIONS, DEFAULT_ALERT_STATUS),
    severity: pickSeverity(search.severity),
    disposition: pick(search.disposition, DISPOSITION_OPTIONS),
    source: pick(search.source, SOURCE_OPTIONS),
    service: pick(search.service, SERVICE_OPTIONS),
    range: pickRange(search.range),
    view: search.view === 'all' ? 'all' : undefined,
  };
}

export function parseDetectionsSearch(search: Record<string, unknown>): DetectionsSearch {
  return {
    severity: pickSeverity(search.severity),
    source: pick(search.source, SOURCE_OPTIONS),
    service: pick(search.service, SERVICE_OPTIONS),
    outcome: pick(search.outcome, OUTCOME_OPTIONS),
    range: pickRange(search.range),
    incident: typeof search.incident === 'string' && search.incident ? search.incident : undefined,
  };
}

/** A filter-control value as it belongs in the URL: defaults become undefined. */
export function urlValue(value: string, fallback = ''): string | undefined {
  return value === fallback ? undefined : value;
}
